// Libraries
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
// Assets
import { getDocumentTypes, postNewDocument } from '@/api/Sherpa/Draw';
import type { PostNewDocumentPayload } from '@/api/Sherpa/Draw/types';
import { getTdmsByProjectPhase } from '@/api/tdm';
import Button from '@/components/UI/Button';
// custom components
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { SherpaProjectReactQueryKey, TdmReactQueryKey } from '@/constants/ReactQueryKeys';
import { useDocumentsTemplateFileUpload } from '@/hooks/useDocumentTemplateFileUpload';
import { notifyUser } from '@/store/Toast';
import type { Deliverable } from '@/types/Constants';
import { getAcceptedFileTypes, sanitizeResponseError } from '@/utils';
import { Phases } from './constant';
import { validationSchema } from './validationSchema';

interface NewDocumentProps {
  handleModal: () => void;
  projectName: string;
}

const drawDefaultValues = {
  phase: '',
  documentType: '',
  documentFile: '',
  deliverableId: '',
  name: '',
  summary: '',
};

const NewBaseDocument = ({ handleModal, projectName }: NewDocumentProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { projectId, drawId } = useParams();
  const { watch } = useFormContext();

  const templateFileRef = useRef<File[]>(null);
  const signAndUploadFiles = useDocumentsTemplateFileUpload();

  const phase = watch('phase');

  //T
  const fetchDeliverables = useQuery({
    queryKey: [TdmReactQueryKey.GET_PROJECT_DELIVERABLES, projectId, phase.value],
    queryFn: () =>
      getTdmsByProjectPhase({
        type: 'deliverables',
        phase: phase.value,
        page: 1,
        perPage: 10,
        projectId: Number(projectId),
      }),
  });
  const deliverables = fetchDeliverables?.data?.data;

  //Tanstack: version 5 of useQuery implementation
  const fetchDocumentTypes = useQuery({
    queryKey: ['get-document-types'],
    queryFn: () => getDocumentTypes(),
  });
  const documentTypes = fetchDocumentTypes?.data?.data;

  useEffect(() => {
    if (signAndUploadFiles.uploadSuccess) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Project Document updated successfully',
          toastType: 'success',
        })
      );
      handleModal();
    }
  }, [signAndUploadFiles.uploadSuccess, handleModal, dispatch]);

  const addDocument = useMutation(
    (data: PostNewDocumentPayload) =>
      postNewDocument({
        data,
        projectId: Number(projectId),
        drawId: Number(drawId),
      }),
    {
      onSuccess: (res) => {
        //Tanstack: version 5 query implementation
        queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_DOCUMENTS] });
        if (templateFileRef.current) {
          signAndUploadFiles.uploadTemplate({
            templateFile: templateFileRef.current,
            drawId: Number(drawId),
            projectId: Number(projectId),
            documentId: Number(res?.data?.id),
          });
        }
        handleModal();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Document created successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const submitNewDocumentHandler = (data: any) => {
    const { documents } = data;
    const transformedData: PostNewDocumentPayload = {
      phaseSlug: data.phase?.value,
      documentTypeSlug: data?.documentType?.value,
      deliverableId: data?.deliverableId ? Number(data?.deliverableId.id) : undefined,
      name: data.name,
      summary: data.summary,
    };
    if (documents?.length) {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      documents?.forEach((file: File) => {
        const acceptedFileTypes = getAcceptedFileTypes().split(',');
        if (acceptedFileTypes.includes(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
            toastType: 'failed',
          })
        );
      }

      if (validFiles.length > 0) {
        templateFileRef.current = validFiles;
      }
    }

    addDocument.mutate(transformedData);
  };

  return (
    <HookForm onSubmit={submitNewDocumentHandler} className='space-y-2 grid grid-cols-2 gap-4'>
      <h3 className='text-sm col-span-2'>Project Name: {projectName}</h3>
      <HookReactSingleSelect componentClassName='col-span-1' label='Phase' name='phase' options={Phases} placeholder='' required />
      <HookReactSingleSelect
        componentClassName='col-span-1'
        label='Document Type'
        name='documentType'
        options={documentTypes}
        placeholder=''
        required
      />
      <HookReactSingleSelect
        componentClassName='col-span-2'
        label='Deliverable'
        name='deliverableId'
        options={deliverables}
        getOptionLabel={(option) => (option as Deliverable).name}
        getOptionValue={(option) => (option as Deliverable).id.toString()}
        placeholder=''
      />
      <div className='col-span-2'>
        <HookFileInput label='Document' name='documents' id='documents' multiple />
      </div>
      <HookInput customClassName='col-span-2' name='name' label='Document Name (50 character max)' type='text' />
      <div className='col-span-2'>
        <HookTextarea name='summary' label='Document Summary (200 character max)' rows={2} />
      </div>
      <div className='flex gap-4 mt-5 justify-end col-span-2'>
        <Button
          label='Cancel'
          type='button'
          btnStyle='btn-neutral'
          disabled={addDocument.isLoading || signAndUploadFiles.isLoading}
          onClick={handleModal}
        />
        <Button
          label={`${addDocument.isLoading || signAndUploadFiles.isLoading ? 'Creating...' : 'Add'}`}
          type='submit'
          btnStyle='btn-primary'
          disabled={addDocument.isLoading || signAndUploadFiles.isLoading}
        />
      </div>
    </HookForm>
  );
};

export const NewDocument = (props: NewDocumentProps) => {
  return (
    <Modal>
      <h2 className='mt-0'>New Document</h2>
      <HookFormProvider defaultValues={drawDefaultValues} resolver={yupResolver(validationSchema) as any}>
        <NewBaseDocument {...props} />
      </HookFormProvider>
    </Modal>
  );
};
